import React, { useState, useEffect } from 'react';
import { getApiKeysStatus } from '../../services/adminApi';
import { notify } from '../../utils/notify';
import './Settings.css';

function ApiKeysSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      setLoading(true);
      const response = await getApiKeysStatus();
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to load API keys status:', err);
      notify.error('Failed to load API keys status');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="settings-loading">Loading API keys status...</div>;
  }

  if (!status) {
    return <div className="settings-error">Failed to load API keys status</div>;
  }

  const StatusBadge = ({ isSet }) => (
    <span className={`status-badge ${isSet ? 'set' : 'not-set'}`}>
      {isSet ? '✓ Set' : '✗ Not Set'}
    </span>
  );

  return (
    <div className="settings-page">
      <h2>API Keys & Integrations</h2>
      <p className="settings-note">
        Note: API keys are masked for security. To update keys, modify environment variables and restart the application.
      </p>

      <div className="settings-section">
        <h3>OpenAI</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>API Key</label>
            <div className="config-value">
              <StatusBadge isSet={status.openai.api_key_set} />
              <span className="key-preview">{status.openai.api_key_preview}</span>
            </div>
          </div>
          <div className="config-item">
            <label>Model</label>
            <div className="config-value">{status.openai.model}</div>
          </div>
          <div className="config-item">
            <label>Embedding Model</label>
            <div className="config-value">{status.openai.embedding_model}</div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Anthropic</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>API Key</label>
            <div className="config-value">
              <StatusBadge isSet={status.anthropic.api_key_set} />
              <span className="key-preview">{status.anthropic.api_key_preview}</span>
            </div>
          </div>
          <div className="config-item">
            <label>Model</label>
            <div className="config-value">{status.anthropic.model}</div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Creatify.ai</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>API Key</label>
            <div className="config-value">
              <StatusBadge isSet={status.creatify.api_key_set} />
              <span className="key-preview">{status.creatify.api_key_preview}</span>
            </div>
          </div>
          <div className="config-item">
            <label>API URL</label>
            <div className="config-value">{status.creatify.api_url}</div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Meta (Facebook/Instagram)</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>App ID</label>
            <div className="config-value">
              <StatusBadge isSet={status.meta.app_id_set} />
              {status.meta.app_id_set && <span className="key-preview">{status.meta.app_id_preview}</span>}
            </div>
          </div>
          <div className="config-item">
            <label>App Secret</label>
            <div className="config-value">
              <StatusBadge isSet={status.meta.app_secret_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Access Token</label>
            <div className="config-value">
              <StatusBadge isSet={status.meta.access_token_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Business ID</label>
            <div className="config-value">
              <StatusBadge isSet={status.meta.business_id_set} />
            </div>
          </div>
          <div className="config-item">
            <label>API Version</label>
            <div className="config-value">{status.meta.api_version}</div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>TikTok</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>Client Key</label>
            <div className="config-value">
              <StatusBadge isSet={status.tiktok.client_key_set} />
              {status.tiktok.client_key_set && <span className="key-preview">{status.tiktok.client_key_preview}</span>}
            </div>
          </div>
          <div className="config-item">
            <label>Client Secret</label>
            <div className="config-value">
              <StatusBadge isSet={status.tiktok.client_secret_set} />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>WhatsApp Business API</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>Phone Number ID #1</label>
            <div className="config-value">
              <StatusBadge isSet={status.whatsapp.phone_number_id_1_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Phone Number ID #2</label>
            <div className="config-value">
              <StatusBadge isSet={status.whatsapp.phone_number_id_2_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Access Token</label>
            <div className="config-value">
              <StatusBadge isSet={status.whatsapp.access_token_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Verify Token</label>
            <div className="config-value">
              <StatusBadge isSet={status.whatsapp.verify_token_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Webhook Verify Token</label>
            <div className="config-value">
              <StatusBadge isSet={status.whatsapp.webhook_verify_token_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Embedded Signup Config ID</label>
            <div className="config-value">
              <StatusBadge isSet={status.whatsapp.embedded_signup_config_id_set} />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>AWS</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>Access Key ID</label>
            <div className="config-value">
              <StatusBadge isSet={status.aws.access_key_id_set} />
              {status.aws.access_key_id_set && <span className="key-preview">{status.aws.access_key_id_preview}</span>}
            </div>
          </div>
          <div className="config-item">
            <label>Secret Access Key</label>
            <div className="config-value">
              <StatusBadge isSet={status.aws.secret_access_key_set} />
            </div>
          </div>
          <div className="config-item">
            <label>Region</label>
            <div className="config-value">{status.aws.region}</div>
          </div>
          <div className="config-item">
            <label>S3 Bucket</label>
            <div className="config-value">{status.aws.s3_bucket_name}</div>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <h3>Email (SMTP)</h3>
        <div className="config-grid">
          <div className="config-item">
            <label>SMTP Host</label>
            <div className="config-value">{status.email.smtp_host}</div>
          </div>
          <div className="config-item">
            <label>SMTP Port</label>
            <div className="config-value">{status.email.smtp_port}</div>
          </div>
          <div className="config-item">
            <label>SMTP Username</label>
            <div className="config-value">
              <StatusBadge isSet={status.email.smtp_username_set} />
            </div>
          </div>
          <div className="config-item">
            <label>SMTP Password</label>
            <div className="config-value">
              <StatusBadge isSet={status.email.smtp_password_set} />
            </div>
          </div>
          <div className="config-item">
            <label>From Email</label>
            <div className="config-value">{status.email.from_email}</div>
          </div>
          <div className="config-item">
            <label>Support Email</label>
            <div className="config-value">{status.email.support_email}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiKeysSettings;

